/**
 * Losi Text Provider Mode
 *
 * Resolves the server-side provider for Losi text and tool-chat models.
 *
 * Primary responsibilities:
 * - Default Losi-compatible text requests to Claude on Bedrock
 * - Preserve explicit Anthropic AWS and Copy Azure/Portkey fallbacks
 *
 * Dependencies:
 * - process.env
 */

export type LosiTextProviderMode = 'bedrock' | 'anthropic_aws' | 'copy_azure'
export type LosiTextProviderModeSetting = LosiTextProviderMode | 'auto'

export type LosiTextProviderProfile = {
  anthropic_api_key?: string | null
}

const DEFAULT_PROVIDER_MODE: LosiTextProviderMode = 'bedrock'
const MODE_ENV_NAMES = [
  'LOSI_TEXT_PROVIDER_MODE',
  'LOSI_ANTHROPIC_PROVIDER',
  'LOSI_BACKEND_PROVIDER'
] as const

const DEFAULT_COPY_AZURE_MAX_MODEL = 'gpt-5.5'
const DEFAULT_COPY_AZURE_MINI_MODEL = 'gpt-5-mini'

type EnvMap = Record<string, string | undefined>

// --------------

function readEnv(env: EnvMap, name: string) {
  const value = env[name]?.trim()
  return value ? value : undefined
}

// --------------

function normalizeModeToken(value: string) {
  return value.trim().toLowerCase().replace(/[\s-]+/g, '_')
}

// --------------

export function parseLosiTextProviderMode(
  value: string | null | undefined
): LosiTextProviderModeSetting | undefined {
  if (!value) return undefined

  switch (normalizeModeToken(value)) {
    case 'auto':
      return 'auto'
    case 'bedrock':
    case 'aws_bedrock':
    case 'amazon_bedrock':
      return 'bedrock'
    case 'anthropic':
    case 'anthropic_aws':
    case 'aws_anthropic':
    case 'direct_anthropic':
      return 'anthropic_aws'
    case 'azure':
    case 'copy':
    case 'copy_azure':
    case 'copy_azure_portkey':
    case 'portkey':
    case 'portkey_azure':
      return 'copy_azure'
    default:
      return undefined
  }
}

// --------------

function resolveAutoMode(env: EnvMap): LosiTextProviderMode {
  if (readEnv(env, 'AWS_BEARER_TOKEN_BEDROCK')) return 'bedrock'
  if (readEnv(env, 'ANTHROPIC_API_KEY') || readEnv(env, 'ANTHROPIC_BASE_URL')) {
    return 'anthropic_aws'
  }
  if (readEnv(env, 'PORTKEY_API_KEY')) return 'copy_azure'

  return DEFAULT_PROVIDER_MODE
}

// --------------

export function resolveLosiTextProviderMode(env: EnvMap = process.env) {
  for (const name of MODE_ENV_NAMES) {
    const setting = parseLosiTextProviderMode(readEnv(env, name))

    if (setting === 'auto') return resolveAutoMode(env)
    if (setting) return setting
  }

  return DEFAULT_PROVIDER_MODE
}

// --------------

export function getLosiCopyAzureMaxModel(env: EnvMap = process.env) {
  return readEnv(env, 'LOSI_COPY_AZURE_MAX_MODEL') || DEFAULT_COPY_AZURE_MAX_MODEL
}

// --------------

export function getLosiCopyAzureMiniModel(env: EnvMap = process.env) {
  return readEnv(env, 'LOSI_COPY_AZURE_MINI_MODEL') || DEFAULT_COPY_AZURE_MINI_MODEL
}

// --------------

export function hasLosiTextProviderAuth(
  profile?: LosiTextProviderProfile | null,
  env: EnvMap = process.env
) {
  switch (resolveLosiTextProviderMode(env)) {
    case 'bedrock':
      return Boolean(readEnv(env, 'AWS_BEARER_TOKEN_BEDROCK'))
    case 'anthropic_aws':
      return Boolean(profile?.anthropic_api_key || readEnv(env, 'ANTHROPIC_API_KEY'))
    case 'copy_azure':
      return Boolean(readEnv(env, 'PORTKEY_API_KEY'))
  }
}
